import React from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Bookmark, MapPin, Calendar, Mountain, Share2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { variantUrl } from "./ImageUploader";

const ACTIVITY_COLORS = {
    hiking: "bg-moss/15 text-moss",
    backpacking: "bg-moss/15 text-moss",
    kayaking: "bg-river-blue/15 text-river-blue",
    overlanding: "bg-golden-larch/15 text-golden-larch",
    biking: "bg-ember-orange/15 text-ember-orange",
    climbing: "bg-trail-red/15 text-trail-red",
    camping: "bg-pine-mist/15 text-pine-mist",
    fishing: "bg-river-blue/15 text-river-blue",
    shooting: "bg-golden-larch/15 text-golden-larch",
};

export default function AdventureCard({ adventure, onLike, onSave, matchReasons }) {
    const {
        id,
        title,
        photos = [],
        activity,
        location_name,
        distance_km,
        difficulty,
        author,
        likes_count = 0,
        comments_count = 0,
        saves_count = 0,
        shares_count = 0,
        liked,
        saved,
        created_at,
    } = adventure;
    const hero = photos[0] || "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200";
    // Feed cards display ~600-1200px wide, so the `card` variant (1200px
    // long-edge JPEG) is plenty. Stock Unsplash URLs and other absolutes
    // pass through untouched.
    const heroSrc = hero.startsWith("/api/files/") ? variantUrl(hero, "card") : hero;

    return (
        <article
            data-testid={`adventure-card-${id}`}
            className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group"
        >
            <Link to={`/adventures/${id}`} className="block relative">
                <div className="relative aspect-[16/10] overflow-hidden bg-trail-tan">
                    <img
                        src={heroSrc}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                        <span
                            className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                ACTIVITY_COLORS[activity] || "bg-alpine-mist text-granite"
                            }`}
                        >
                            {activity}
                        </span>
                        {difficulty && (
                            <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-midnight-pine/80 text-white backdrop-blur-sm">
                                {difficulty}
                            </span>
                        )}
                    </div>
                    {distance_km && (
                        <div className="absolute bottom-3 right-3 bg-snowcap/95 backdrop-blur px-3 py-1.5 rounded-md text-sm font-black text-midnight-pine">
                            {distance_km} km
                        </div>
                    )}
                    {shares_count >= 1 && (
                        <div
                            data-testid={`adventure-shares-badge-${id}`}
                            className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-ember-orange/95 text-white backdrop-blur shadow"
                        >
                            <Share2 className="w-3 h-3" /> {shares_count} share{shares_count === 1 ? "" : "s"}
                        </div>
                    )}
                </div>
            </Link>

            <div className="p-5">
                <Link to={`/adventures/${id}`} className="block">
                    <h3 className="font-display font-bold text-lg text-granite leading-tight tracking-tight group-hover:text-ember-orange transition-colors line-clamp-2">
                        {title}
                    </h3>
                </Link>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-gray">
                    {location_name && (
                        <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{location_name}</span>
                        </span>
                    )}
                    {created_at && (
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDistanceToNow(new Date(created_at), { addSuffix: true })}
                        </span>
                    )}
                </div>

                {matchReasons?.length > 0 && (
                    <div
                        data-testid={`adventure-match-reasons-${id}`}
                        className="flex flex-wrap gap-1 mt-3"
                    >
                        {matchReasons.slice(0, 3).map((r) => (
                            <span
                                key={r}
                                className="px-2 py-0.5 rounded-full bg-ember-orange/15 text-ember-orange text-[10px] font-bold capitalize"
                            >
                                {r}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-ridge-line">
                    <Link
                        to={`/profile/${author?.user_id}`}
                        data-testid={`adventure-card-author-${id}`}
                        className="flex items-center gap-2 group/author"
                    >
                        <div className="w-7 h-7 rounded-full overflow-hidden bg-trail-tan">
                            {author?.avatar ? (
                                <img src={author.avatar} alt={author.name} className="w-full h-full object-cover" />
                            ) : (
                                <Mountain className="w-4 h-4 text-midnight-pine m-auto mt-1.5" />
                            )}
                        </div>
                        <span className="text-sm font-semibold text-granite group-hover/author:text-ember-orange transition-colors">
                            {author?.name || "Unknown"}
                        </span>
                    </Link>
                    <div className="flex items-center gap-3 text-sm text-slate-gray">
                        <button
                            data-testid={`adventure-like-${id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onLike?.(adventure);
                            }}
                            className="flex items-center gap-1 min-h-9 py-1 -my-1 hover:text-ember-orange active:scale-95 transition-all"
                            aria-label="Like"
                        >
                            <Heart className={`w-4 h-4 ${liked ? "fill-ember-orange text-ember-orange" : ""}`} />
                            <span className="font-semibold">{likes_count}</span>
                        </button>
                        <span className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            <span className="font-semibold">{comments_count}</span>
                        </span>
                        <button
                            data-testid={`adventure-save-${id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onSave?.(adventure);
                            }}
                            className="flex items-center gap-1 min-h-9 py-1 -my-1 hover:text-ember-orange active:scale-95 transition-all"
                            aria-label="Save"
                        >
                            <Bookmark className={`w-4 h-4 ${saved ? "fill-ember-orange text-ember-orange" : ""}`} />
                            <span className="font-semibold">{saves_count}</span>
                        </button>
                    </div>
                </div>
            </div>
        </article>
    );
}
